import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { cleanTranscript } from '../services/openAIService';
import { patientReviewPath, patientRecordPath, patientPath, ROUTES } from '../constants/routes';
import { getAllPatients } from '../constants/mockPatients';
import { transcriptHasClinicalSignal } from '../utils/clinicalContent';
import { buildGenerationResult } from '../utils/generationPipeline';
import {
  splitTranscriptByPatients,
} from '../utils/multiPatientTranscript';
import {
  processMultiPatientSegments,
} from '../utils/multiPatientGeneration';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { clearStoredAccessCode } from './AccessCodeScreen';
import NoteApplyingSkeleton from '../components/review/NoteApplyingSkeleton';
import { REVIEW_PAGE_BG } from '../components/review/reviewStyles';
import ReviewModal from '../layouts/ReviewModal';
import { persistClinicalDraft } from '../services/persistClinical';

async function attachClinicalIdsToDraft(draft) {
  const primary = draft.generatedNotes?.find((entry) => entry.note) || draft.generatedNotes?.[0];
  const clinicalIds = await persistClinicalDraft(draft, {
    noteJson: primary?.note || draft.openaiNote,
    noteType: primary?.type || draft.noteType,
    audioDurationSeconds: draft.recordingDurationSeconds,
    recordingEndAt: draft.recordingEndAt || new Date().toISOString(),
  });
  if (!clinicalIds) return draft;
  return { ...draft, clinicalIds };
}

async function goToReview(navigate, setDraftGeneration, draft, options = {}) {
  const withIds = await attachClinicalIdsToDraft(draft);
  setDraftGeneration(withIds);
  navigateToReview(navigate, withIds, options);
}

function toIsoDatetime(value) {
  if (!value) return new Date().toISOString();
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

function handleApiGenerationError(err, navigate, setGenerationError) {
  if (err.status === 400) {
    const detail = String(err.message || err.error?.detail || '');
    setGenerationError(
      detail.includes('Recording too long')
        ? 'Recording too long. Maximum recording length is 3 minutes.'
        : (detail || 'Note generation failed. Please try again.')
    );
    return true;
  }

  if (err.status === 429) {
    if (err.error?.retryAfter === 86400) {
      setGenerationError('Daily limit reached. Contact Abdul to continue.');
    } else {
      setGenerationError('Too many requests. Please wait a moment and try again.');
    }
    return true;
  }

  if (err.status === 401) {
    clearStoredAccessCode();
    navigate(ROUTES.LOGIN, { replace: true });
    return true;
  }

  setGenerationError('Note generation failed. Please try again.');
  return true;
}

function navigateToPatientSelect(navigate, {
  result,
  reviewQueue = [],
  multiPatientContext = null,
  recordingDurationSeconds = null,
}) {
  navigate(ROUTES.PATIENT_SELECT, {
    state: {
      generationResult: result,
      patientMatch: result.patientMatch,
      patientHints: result.patientHints,
      reviewQueue,
      reviewQueueIndex: reviewQueue.length,
      multiPatientContext,
      recordingDurationSeconds: recordingDurationSeconds ?? result?.recordingDurationSeconds,
    },
    replace: true,
  });
}

function navigateToReview(navigate, result, options = {}) {
  const {
    reviewQueue = null,
    reviewQueueIndex = 0,
    patientRouteId = null,
    recordingDurationSeconds = null,
  } = options;
  const reviewPath = patientRouteId ? patientReviewPath(patientRouteId) : ROUTES.REVIEW;
  navigate(reviewPath, {
    state: {
      ...result,
      reviewQueue,
      reviewQueueIndex,
      recordingDurationSeconds: recordingDurationSeconds ?? result?.recordingDurationSeconds,
    },
    replace: true,
  });
}

export default function GeneratingScreen() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setDraftGeneration, admittedPatients, taskDocSession, setTaskDocumentationDraft } = useSession();
  const { t } = useLanguage();
  const [statusText, setStatusText] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [retryToken, setRetryToken] = useState(0);

  const transcript = location.state?.transcript || '';
  const recordPath = id ? patientRecordPath(id) : ROUTES.FREE_RECORD;
  const encounterIso = useMemo(
    () => toIsoDatetime(location.state?.encounterDateTime),
    [location.state?.encounterDateTime]
  );
  const encounterGroupIdRef = useRef(null);
  if (!encounterGroupIdRef.current) {
    encounterGroupIdRef.current = `${Date.now()}`;
  }
  const encounterGroupId = encounterGroupIdRef.current;
  const hasStartedRef = useRef(false);
  const taskCompletionMeta = useMemo(
    () => ({
      manualTaskCompletionTaskId: location.state?.manualTaskCompletionTaskId || null,
      manualTaskCompletionType: location.state?.manualTaskCompletionType || null,
    }),
    [location.state?.manualTaskCompletionTaskId, location.state?.manualTaskCompletionType]
  );

  const withTaskCompletion = (result) => {
    if (!result) return result;
    const next = {
      ...result,
      recordingDurationSeconds:
        location.state?.recordingDurationSeconds || result.recordingDurationSeconds || null,
    };
    if (!taskCompletionMeta.manualTaskCompletionTaskId) return next;
    return {
      ...next,
      manualTaskCompletionTaskId: taskCompletionMeta.manualTaskCompletionTaskId,
      manualTaskCompletionType: taskCompletionMeta.manualTaskCompletionType,
    };
  };

  useEffect(() => {
    setStatusText(t('analyzingTranscript'));
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    async function runGeneration() {
      const redirectNoClinicalContent = () => {
        setDraftGeneration(null);
        navigate(recordPath, {
          replace: true,
          state: { recordingError: t('noClinicalContent') },
        });
      };

      try {
        const rawTranscript = transcript.trim();
        setStatusText(t('analyzingTranscript'));

        let cleanedTranscript = rawTranscript;
        let cleanupFailed = false;

        try {
          cleanedTranscript = await cleanTranscript(rawTranscript);
          if (!cleanedTranscript?.trim()) {
            cleanedTranscript = rawTranscript;
            cleanupFailed = true;
          }
        } catch (err) {
          if (err.status === 429 || err.status === 401 || err.status === 400) throw err;
          console.warn('Transcript cleanup failed, using raw transcript:', err);
          cleanedTranscript = rawTranscript;
          cleanupFailed = true;
        }

        if (cancelled) return;

        if (!transcriptHasClinicalSignal(rawTranscript, cleanedTranscript)) {
          redirectNoClinicalContent();
          return;
        }

        const resumeMultiPatient = location.state?.resumeMultiPatient;
        if (resumeMultiPatient) {
          const segmentOutcome = await processMultiPatientSegments({
            segments: resumeMultiPatient.segments,
            startIndex: resumeMultiPatient.startIndex,
            initialQueue: resumeMultiPatient.queue || [],
            admittedPatients,
            encounterIso: resumeMultiPatient.encounterIso || encounterIso,
            encounterGroupId: resumeMultiPatient.encounterGroupId || encounterGroupId,
            cleanupFailed: resumeMultiPatient.cleanupFailed,
            sourceTranscriptRaw: resumeMultiPatient.sourceTranscriptRaw || rawTranscript,
            sourceTranscriptCleaned:
              resumeMultiPatient.sourceTranscriptCleaned || cleanedTranscript,
            onProgress: (index, total) => {
              if (cancelled) return;
              setStatusText(
                t('generatingMultiPatientProgress')
                  .replace('{current}', String(index + 1))
                  .replace('{total}', String(total))
              );
            },
          });

          if (cancelled) return;

          if (segmentOutcome.status === 'needs_selection') {
            setDraftGeneration(withTaskCompletion(segmentOutcome.result));
            navigateToPatientSelect(navigate, {
              result: withTaskCompletion(segmentOutcome.result),
              reviewQueue: segmentOutcome.queue,
              multiPatientContext: segmentOutcome.multiPatientContext,
              recordingDurationSeconds: location.state?.recordingDurationSeconds,
            });
            return;
          }

          const queue = segmentOutcome.queue || [];
          if (queue.length >= 2) {
            await goToReview(navigate, setDraftGeneration, withTaskCompletion(queue[0]), {
              reviewQueue: queue,
              reviewQueueIndex: 0,
              recordingDurationSeconds: location.state?.recordingDurationSeconds,
            });
            return;
          }

          if (queue.length === 1) {
            await goToReview(navigate, setDraftGeneration, withTaskCompletion(queue[0]), {
              recordingDurationSeconds: location.state?.recordingDurationSeconds,
            });
            return;
          }

          redirectNoClinicalContent();
          return;
        }

        // Multi-patient: split first, then run normal note-type detection on EACH segment
        // (same path as a single-patient recording). Never force handover for the whole clip.
        if (!id && !location.state?.forcedNoteTypes?.length) {
          const segments = splitTranscriptByPatients(cleanedTranscript, getAllPatients(admittedPatients));
          if (segments.length >= 2) {
            const segmentOutcome = await processMultiPatientSegments({
              segments,
              admittedPatients,
              encounterIso,
              encounterGroupId,
              cleanupFailed,
              sourceTranscriptRaw: rawTranscript,
              sourceTranscriptCleaned: cleanedTranscript,
              onProgress: (index, total) => {
                if (cancelled) return;
                setStatusText(
                  t('generatingMultiPatientProgress')
                    .replace('{current}', String(index + 1))
                    .replace('{total}', String(total))
                );
              },
            });

            if (cancelled) return;

            if (segmentOutcome.status === 'needs_selection') {
              setDraftGeneration(withTaskCompletion(segmentOutcome.result));
              navigateToPatientSelect(navigate, {
                result: withTaskCompletion(segmentOutcome.result),
                reviewQueue: segmentOutcome.queue,
                multiPatientContext: segmentOutcome.multiPatientContext,
                recordingDurationSeconds: location.state?.recordingDurationSeconds,
              });
              return;
            }

            const queue = segmentOutcome.queue || [];
            if (queue.length >= 2) {
              await goToReview(navigate, setDraftGeneration, withTaskCompletion(queue[0]), {
                reviewQueue: queue,
                reviewQueueIndex: 0,
                recordingDurationSeconds: location.state?.recordingDurationSeconds,
              });
              return;
            }

            if (queue.length === 1) {
              await goToReview(navigate, setDraftGeneration, withTaskCompletion(queue[0]), {
                recordingDurationSeconds: location.state?.recordingDurationSeconds,
              });
              return;
            }

            redirectNoClinicalContent();
            return;
          }
        }

        setStatusText(t('detectingNoteType'));

        const forcedFromTask = taskCompletionMeta.manualTaskCompletionType
          ? [taskCompletionMeta.manualTaskCompletionType]
          : null;

        // Multi-note detection runs inside buildGenerationResult:
        // when medication_admin is detected, progress_note is always generated alongside it.
        // Task documentation already knows its note type — skip detection.
        const result = await buildGenerationResult({
          rawTranscript,
          cleanedTranscript,
          cleanupFailed,
          admittedPatients,
          patientId: id || null,
          forcedNoteTypes: location.state?.forcedNoteTypes || forcedFromTask || null,
          admissionMeta: location.state?.admissionMeta,
          encounterIso,
          encounterGroupId,
        });

        if (cancelled) return;

        if (!result || result.error === 'no_clinical_content') {
          redirectNoClinicalContent();
          return;
        }

        const draft = withTaskCompletion(result);

        if (draft.needsPatientSelection) {
          setDraftGeneration(draft);
          navigateToPatientSelect(navigate, { result: draft });
          return;
        }

        const handoverEntry = draft.generatedNotes?.find((entry) => entry.type === 'handover' && entry.note);
        const isMultiHandover = (handoverEntry?.note?.patients?.length || 0) > 1;

        // Multi-patient handovers always review on one scrollable page (avatar strip is display-only).
        if (isMultiHandover) {
          await goToReview(navigate, setDraftGeneration, draft, { patientRouteId: id || null });
          return;
        }

        if (!id && !draft.patientId && !isMultiHandover) {
          setDraftGeneration(draft);
          navigateToPatientSelect(navigate, { result: draft });
          return;
        }

        const taskDocId = taskCompletionMeta.manualTaskCompletionTaskId;
        if (
          taskDocId
          && taskDocSession?.taskId === taskDocId
          && (id || taskDocSession.patientId)
        ) {
          setTaskDocumentationDraft(draft);
          setDraftGeneration(null);
          navigate(patientPath(id || taskDocSession.patientId), { replace: true });
          return;
        }

        await goToReview(navigate, setDraftGeneration, draft, { patientRouteId: id || null });
      } catch (err) {
        if (!cancelled) {
          console.error('Note generation failed:', err);
          if (handleApiGenerationError(err, navigate, setGenerationError)) {
            return;
          }
        }
      }
    }

    if (!transcript?.trim() && !location.state?.resumeMultiPatient) {
      navigate(ROUTES.PATIENTS);
      return undefined;
    }

    if (hasStartedRef.current) return undefined;
    hasStartedRef.current = true;

    runGeneration();

    return () => {
      cancelled = true;
    };
  }, [
    admittedPatients,
    transcript,
    id,
    navigate,
    recordPath,
    setDraftGeneration,
    t,
    encounterIso,
    encounterGroupId,
    location.state?.forcedNoteTypes,
    location.state?.admissionMeta,
    location.state?.resumeMultiPatient,
    retryToken,
    taskDocSession,
    setTaskDocumentationDraft,
    taskCompletionMeta.manualTaskCompletionTaskId,
  ]);

  const handleRetry = () => {
    setGenerationError('');
    setStatusText(t('analyzingTranscript'));
    hasStartedRef.current = false;
    setRetryToken((value) => value + 1);
  };

  return (
    <ReviewModal>
      <div className={`flex h-full min-h-0 flex-col overflow-hidden ${REVIEW_PAGE_BG}`}>
        {generationError ? (
          <div className="flex flex-1 items-center justify-center px-4">
            <div className="w-full max-w-md rounded-xl border border-critical bg-critical-light px-4 py-3 text-sm text-critical-dark">
              <p>{generationError}</p>
              <button
                type="button"
                onClick={handleRetry}
                className="mt-3 rounded-lg bg-critical px-4 py-2 text-sm font-semibold text-white"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-4">
            <NoteApplyingSkeleton />
          </div>
        )}
      </div>
    </ReviewModal>
  );
}
